import { Heart, Users, Compass, Shield } from 'lucide-react';
import { CategoryConfig } from '@/types/category';
import { 
  createTherapeuticSexualWellnessSafetyPrompt,
  createCouplesTherapySafetyPrompt,
  createRelationshipDecisionsSafetyPrompt,
  createAddictionRecoverySafetyPrompt
} from '@/utils/guardrails';

export const sexualWellnessWomenConfig: CategoryConfig = {
  name: "Women's Sexual Wellness",
  color: "from-rose-400 to-pink-600",
  icon: Heart,
  description: "Therapeutic sessions for women addressing libido, sexual pleasure, trauma healing, and shame reduction",
  defaultPrompt: `${createTherapeuticSexualWellnessSafetyPrompt()}

Create a deeply therapeutic meditation session for a woman working on sexual wellness, addressing themes of:
- Reclaiming personal power and body autonomy
- Healing from sexual trauma or shame
- Reconnecting with natural desires and pleasure
- Building self-compassion and acceptance
- Strengthening the mind-body connection
- Honoring personal boundaries and comfort levels

This session should be:
- Trauma-informed and shame-free
- Empowering and strength-based
- Gentle and self-paced
- Grounded in safety and consent
- Focused on healing and wholeness
- Honoring Michele's therapeutic expertise in sexual wellness

Include grounding practices, breathwork, and body awareness techniques that support sexual healing and empowerment.`,
  samplePrompt: "A guided meditation for sexual healing and empowerment",
  requiresAgeVerification: false // Michele handles age verification
};

export const couplesConnectionConfig: CategoryConfig = {
  name: "Couples Connection",
  color: "from-purple-400 to-indigo-600", 
  icon: Users,
  description: "Therapeutic sessions for couples rebuilding emotional and physical intimacy",
  defaultPrompt: `${createCouplesTherapySafetyPrompt()}

Create a therapeutic meditation session for a couple working on rebuilding their emotional and physical connection:
- Fostering emotional safety and vulnerability
- Rebuilding trust and understanding
- Enhancing communication and empathy
- Reconnecting physically and intimately
- Healing relationship wounds together
- Strengthening their partnership bond

This session should be:
- Neutral and non-blaming toward both partners
- Focused on connection and understanding
- Supportive of gradual vulnerability
- Honoring both individual and relationship needs
- Emphasizing teamwork in healing
- Reflecting Michele's expertise in couples therapy

Include practices for synchronized breathing, loving-kindness, and gentle physical connection that both partners can experience together.`,
  samplePrompt: "A guided meditation for couples reconnection and intimacy",
  requiresAgeVerification: false // Michele handles age verification
};

export const relationshipDecisionsConfig: CategoryConfig = {
  name: "Relationship Decisions", 
  color: "from-amber-400 to-orange-600",
  icon: Compass,
  description: "Supportive meditation for those contemplating relationship decisions (stay or leave)",
  defaultPrompt: `${createRelationshipDecisionsSafetyPrompt()}

Create a non-directive meditation session for someone contemplating whether to stay in or leave their relationship:
- Connecting with inner wisdom and personal values
- Finding clarity amidst emotional confusion
- Honoring the complexity of relationship decisions
- Building courage to face difficult questions
- Practicing self-compassion during uncertainty
- Accessing personal truth and authentic desires

This session should be:
- Completely neutral about outcomes (not pushing stay or leave)
- Focused on inner wisdom and values alignment
- Supportive of the decision-making process
- Gentle with emotional difficulty and ambiguity
- Respectful of personal timing and readiness
- Honoring Michele's expertise in relationship dynamics

Include grounding practices, values clarification, and heart-centered awareness techniques that support authentic decision-making.`,
  samplePrompt: "A meditation for relationship clarity and inner wisdom",
  requiresAgeVerification: false // Michele handles age verification
};

export const addictionRecoveryMenConfig: CategoryConfig = {
  name: "Men's Addiction Recovery",
  color: "from-teal-400 to-cyan-600",
  icon: Shield, 
  description: "Recovery-focused sessions for men dealing with sex/porn addiction",
  defaultPrompt: `${createAddictionRecoverySafetyPrompt()}

Create a recovery-oriented meditation session for a man working through sex/porn addiction:
- Addressing underlying emotional needs and trauma
- Building healthy coping mechanisms
- Reconnecting with authentic emotions and relationships
- Healing shame and self-compassion
- Developing emotional regulation skills
- Finding meaning and connection beyond addictive behaviors

This session should be:
- Completely shame-free and non-judgmental
- Focused on healing rather than just abstinence
- Recognizing underlying trauma and emotional needs
- Normalizing recovery as a learning process
- Male-specific in addressing emotional disconnection
- Honoring Michele's expertise in addiction recovery

Include practices for emotional awareness, self-compassion, grounding, and building authentic connections that support long-term recovery and healing.`,
  samplePrompt: "A recovery meditation focusing on healing and emotional connection",
  requiresAgeVerification: false // Michele handles age verification
};